import * as React from 'react';
import { t } from '@reos/i18n';
import { Check, CircleAlert } from 'lucide-react';
import type {
  ChecklistPointDto,
  ChecklistTemplateDto,
  ChecklistTemplateKind,
  DictationSuggestionDto,
  IntakeContextDto,
} from '../../lib/field-intake-api.js';
import type { ChecklistState } from '../../lib/field-intake-checklist.js';
import type { Recorder } from '../../lib/field-intake-recorder.js';
import { groupScopeKey, suggestionForPoint, type DictationStates } from '../../lib/intake-dictation.js';
import { DictationMic, DictationSuggestion } from './IntakeDictation.js';
import {
  checklistProgress,
  coveredPoints,
  groupedPoints,
  pointAnswers,
  prefillOf,
  type ChecklistMode,
} from '../../lib/intake-checklist.js';
import { formatValue } from './values.js';
import { originCaption, provenanceOrigin } from '../../lib/case-origin.js';

/**
 * Krok NAB2 — ZALOZKA „CHECKLIST" NABORU (zadani R.B. 14. 10. 2026).
 *
 * Makler klepne, vybere ze seznamu, nebo dopise („Jiné…"). Kazda odpoved jde
 * TOUTEZ cestou jako odskrtnuty bod v NAB1: do stavu obrazovky a offline
 * frontou do relace naboru, odkud se potvrzenim zapise na kartu nemovitosti.
 * Zadna druha cesta zapisu tu nevznika.
 *
 * Mobil prvni: kazde tlacitko je cil klepnuti >= 44 px (`--rh-touch-h`),
 * vyber je jedno klepnuti, „Jiné…" otevre textove pole. Skupiny jsou v poradi,
 * jak se jde bytem (sablona), a kazda nese „vyplneno/celkem".
 *
 * RYCHLY REZIM (jen body R + to, co Sreality vyzaduje) je vychozi; prepinac
 * „Úplný" ukaze vsechny body sablony.
 */

const tr = (key: Parameters<typeof t>[0], values?: Record<string, string | number>): string =>
  t(key, { locale: 'cs', ...(values !== undefined ? { values } : {}) });

/** Hodnota bodu -> lidsky zapis (ciselnik ma popisek, cislo jednotku). */
function answerLabel(point: ChecklistPointDto, value: unknown): string {
  if (Array.isArray(value)) {
    return value
      .map((item) => point.options.find((option) => option.value === item)?.label ?? String(item))
      .join(', ');
  }
  const option = point.options.find((candidate) => candidate.value === value);
  if (option !== undefined) return option.label;
  if (typeof value === 'boolean') return tr(value ? 'webAdmin.fieldIntake.value.yes' : 'webAdmin.fieldIntake.value.no');
  if (point.factKey !== null) return formatValue(point.factKey, value);
  return String(value);
}

/** Zapis textu/cisla/datumu podle typu faktu; `null` = hodnota nesedi. */
export function parsePointValue(point: ChecklistPointDto, raw: string): unknown {
  const text = raw.trim();
  if (text === '') return null;
  switch (point.valueType) {
    case 'number': {
      const n = Number(text.replace(/\s/g, '').replace(',', '.'));
      return Number.isFinite(n) ? n : null;
    }
    case 'integer': {
      const n = Number(text.replace(/\s/g, ''));
      return Number.isInteger(n) ? n : null;
    }
    case 'money': {
      const digits = text.replace(/\s/g, '').replace(/kč$/i, '').replace(',', '.');
      return /^\d+(\.\d{1,4})?$/.test(digits) ? { amount: digits, currency: 'CZK' } : null;
    }
    case 'list':
      return text
        .split(/[;,\n]/)
        .map((item) => item.trim())
        .filter((item) => item !== '')
        .slice(0, 50);
    case 'boolean':
      return null;
    default:
      return text.slice(0, 500);
  }
}

function Point({
  point,
  value,
  covered,
  prefill,
  suggestion,
  onAnswer,
  onAcceptSuggestion,
  onDismissSuggestion,
  onClear,
}: {
  readonly point: ChecklistPointDto;
  readonly value: unknown;
  readonly covered: boolean;
  readonly prefill: {
    readonly value: unknown;
    readonly sourceType: string;
    readonly provenanceStatus: string;
  } | null;
  /** Krok NAB4 — navrh z diktatu u tohoto bodu („sviti", s citaci vety). */
  readonly suggestion: DictationSuggestionDto | null;
  readonly onAnswer: (value: unknown) => void;
  readonly onAcceptSuggestion: (suggestion: DictationSuggestionDto) => void;
  readonly onDismissSuggestion: (suggestion: DictationSuggestionDto) => void;
  readonly onClear: () => void;
}): React.JSX.Element {
  const [other, setOther] = React.useState(false);
  const [raw, setRaw] = React.useState('');
  const [invalid, setInvalid] = React.useState(false);
  const answered = value !== undefined && value !== null;
  /*
   * Bod, ktery checklist NEZAPISUJE (druh obchodu drzi zakazka, adresa karta
   * nemovitosti, popis a doba publikovani koncept inzeratu). Ukazuje se, aby
   * makler videl uplnost, ale NEMA zaskrtavatka — tlacitko, ktere nereaguje,
   * byl presne nalez R.B. z NAB1.
   */
  const readOnly = point.factKey === null;
  const chips = readOnly ? [] : point.options.filter((option) => !option.meansUnknown && option.value !== null);
  const multi = point.control === 'multiselect';

  const save = (text: string): void => {
    const parsed = parsePointValue(point, text);
    if (parsed === null) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    setRaw('');
    setOther(false);
    onAnswer(parsed);
  };

  const toggleChip = (optionValue: string | number | boolean): void => {
    if (!multi) {
      onAnswer(optionValue);
      return;
    }
    const current = Array.isArray(value) ? (value as unknown[]) : [];
    const next = current.includes(optionValue)
      ? current.filter((item) => item !== optionValue)
      : [...current, optionValue];
    if (next.length === 0) onClear();
    else onAnswer(next);
  };

  return (
    <div
      className={`rh-fi-point rh-fi-cl__point${suggestion !== null ? ' rh-fi-point--suggested' : ''}`}
      data-testid={`nab2-point-${point.id}`}
      data-answered={answered ? 'yes' : 'no'}
      data-suggested={suggestion !== null ? 'yes' : 'no'}
    >
      <div className="rh-fi-cl__q">
        <span className={`rh-fi-check${answered || covered ? ' is-on' : ''}`} aria-hidden="true">
          {answered || covered ? <Check size={18} strokeWidth={3} /> : null}
        </span>
        <span className="rh-v4-row__text">
          <span className="rh-v4-row__title">{point.question}</span>
          <span className="rh-v4-row__sub">
            {point.sreality?.required === true ? (
              <strong className="rh-fi-cl__tag rh-fi-cl__tag--portal" data-testid={`nab2-tag-sreality-${point.id}`}>
                {tr('webAdmin.fieldIntake.checklistTab.tagSreality')}
              </strong>
            ) : null}
            {point.contractKey !== null ? (
              <strong className="rh-fi-cl__tag rh-fi-cl__tag--contract">{tr('webAdmin.fieldIntake.checklistTab.tagContract')}</strong>
            ) : null}
            {point.where !== '' ? <span className="rh-fi-cl__where">{point.where}</span> : null}
          </span>
        </span>
        {answered && !readOnly ? (
          <button
            type="button"
            className="rh-v4-btn rh-v4-btn--plain"
            onClick={onClear}
            data-testid={`nab2-clear-${point.id}`}
          >
            {tr('webAdmin.fieldIntake.checklistTab.clear')}
          </button>
        ) : null}
      </div>

      {readOnly ? (
        <div className="rh-fi-cl__where" data-testid={`nab2-elsewhere-${point.id}`}>
          {tr(`webAdmin.fieldIntake.checklistTab.providedBy.${point.providedBy ?? 'case'}` as Parameters<typeof t>[0])}
        </div>
      ) : null}

      {answered ? (
        <div className="rh-fi-cl__answer" data-testid={`nab2-answer-${point.id}`}>
          {answerLabel(point, value)}
          {point.unit !== null && point.valueType === 'number' ? ` ${point.unit}` : ''}
        </div>
      ) : null}

      {/* Krok NAB4: navrh z diktatu — nic se nezapsalo, dokud makler neklepne. */}
      {suggestion !== null && !readOnly ? (
        <DictationSuggestion
          suggestion={suggestion}
          testid={`nab4-point-${point.id}`}
          onAccept={() => onAcceptSuggestion(suggestion)}
          onDismiss={() => onDismissSuggestion(suggestion)}
        />
      ) : null}

      {prefill !== null && !answered ? (
        <div className="rh-fi-cl__prefill" data-testid={`nab2-prefill-${point.id}`}>
          <span>
            {tr('webAdmin.fieldIntake.checklistTab.prefill', {
              value: answerLabel(point, prefill.value),
              /* Krok P8F6 bod 1: lidsky puvod, ne kod zdroje („user"). */
              source: originCaption(
                provenanceOrigin({ status: prefill.provenanceStatus, sourceType: prefill.sourceType }),
              ),
            })}
          </span>
          <button
            type="button"
            className="rh-v4-btn rh-v4-btn--chip"
            onClick={() => onAnswer(prefill.value)}
            data-testid={`nab2-prefill-confirm-${point.id}`}
          >
            {tr('webAdmin.fieldIntake.checklistTab.prefillConfirm')}
          </button>
        </div>
      ) : null}

      {chips.length > 0 ? (
        <div className="rh-fi-cl__chips">
          {chips.map((option) => {
            const on = multi
              ? Array.isArray(value) && (value as unknown[]).includes(option.value)
              : value === option.value;
            return (
              <button
                key={String(option.value)}
                type="button"
                className={`rh-v4-btn rh-v4-btn--chip rh-fi-cl__chip${on ? ' is-on' : ''}`}
                aria-pressed={on}
                onClick={() => toggleChip(option.value as string | number | boolean)}
                data-testid={`nab2-opt-${point.id}-${String(option.value)}`}
              >
                {option.label}
              </button>
            );
          })}
          {/*
            „Nevím" neni „ne": bod zustava nezjisteny (hodnota se nezapise),
            jen ho makler schova z prehledu rozdelanych odpovedi.
          */}
          {point.options.some((option) => option.meansUnknown) ? (
            <button
              type="button"
              className="rh-v4-btn rh-v4-btn--chip rh-fi-cl__chip rh-fi-cl__chip--unknown"
              onClick={onClear}
              data-testid={`nab2-unknown-${point.id}`}
            >
              {tr('webAdmin.fieldIntake.checklistTab.unknown')}
            </button>
          ) : null}
          {point.allowOther ? (
            <button
              type="button"
              className="rh-v4-btn rh-v4-btn--chip rh-fi-cl__chip"
              onClick={() => setOther((open) => !open)}
              data-testid={`nab2-other-${point.id}`}
            >
              {tr('webAdmin.fieldIntake.checklistTab.other')}
            </button>
          ) : null}
        </div>
      ) : null}

      {(chips.length === 0 && !readOnly) || other ? (
        <div className="rh-fi-cl__input">
          <input
            className="rh-input rh-fi-input rh-fi-input--grow"
            value={raw}
            type={point.control === 'date' ? 'date' : 'text'}
            inputMode={
              point.valueType === 'number' || point.valueType === 'integer'
                ? 'decimal'
                : point.valueType === 'money'
                  ? 'numeric'
                  : 'text'
            }
            aria-label={point.question}
            placeholder={point.unit ?? ''}
            onChange={(e) => setRaw(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') save(raw);
            }}
            data-testid={`nab2-value-${point.id}`}
          />
          <button
            type="button"
            className="rh-v4-btn rh-v4-btn--chip"
            onClick={() => save(raw)}
            data-testid={`nab2-save-${point.id}`}
          >
            {tr('webAdmin.fieldIntake.checklist.valueSave')}
          </button>
        </div>
      ) : null}

      {invalid ? (
        <p className="rh-fi-note rh-fi-note--warn" role="alert" data-testid={`nab2-invalid-${point.id}`}>
          {tr('webAdmin.fieldIntake.checklist.valueInvalid')}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Krok NAB6 bod 3 — PREPINAC SABLONY. Predvolba jde ze zakazky; kdyz ji
 * makler prepne rucne (nebo ji ze zakazky nejde poznat), checklist to RIKA
 * NAHLAS — jinak by nabiral najem podle sablony na prodej a zjistil to az
 * u zastaveneho exportu.
 */
function TemplateSwitch({
  current,
  expected,
  available,
  onPick,
}: {
  readonly current: ChecklistTemplateKind;
  readonly expected: ChecklistTemplateKind | null;
  readonly available: readonly ChecklistTemplateKind[];
  readonly onPick: (kind: ChecklistTemplateKind) => void;
}): React.JSX.Element {
  const name = (kind: ChecklistTemplateKind): string =>
    tr(`webAdmin.fieldIntake.checklistTab.templateName.${kind}` as Parameters<typeof t>[0]);
  return (
    <div className="rh-fi-cl__templates" data-testid="nab6-template-switch">
      <span>{tr('webAdmin.fieldIntake.checklistTab.templateLabel')}</span>
      {available.map((kind) => (
        <button
          key={kind}
          type="button"
          className={`rh-v4-btn rh-v4-btn--chip${current === kind ? ' is-on' : ''}`}
          aria-pressed={current === kind}
          onClick={() => onPick(kind)}
          data-testid={`nab6-template-${kind}`}
        >
          {name(kind)}
        </button>
      ))}
      {expected === null ? (
        <p className="rh-fi-note rh-fi-note--warn" role="alert" data-testid="nab6-template-unknown">
          <CircleAlert size={18} aria-hidden="true" />
          {tr('webAdmin.fieldIntake.checklistTab.templateUnknownWarning')}
        </p>
      ) : null}
      {expected !== null && expected !== current ? (
        <p className="rh-fi-note rh-fi-note--warn" role="alert" data-testid="nab6-template-warning">
          <CircleAlert size={18} aria-hidden="true" />
          {tr('webAdmin.fieldIntake.checklistTab.templateManualWarning', {
            name: name(current),
            expected: name(expected),
          })}
        </p>
      ) : null}
    </div>
  );
}

export function IntakeChecklist({
  template,
  context,
  templateKind = null,
  expectedKind = null,
  onTemplateKind,
  marks,
  onMark,
  onUnmark,
  recorder,
  dictations = {},
  onDictate,
  onAcceptSuggestion,
  onDismissSuggestion,
}: {
  readonly template: ChecklistTemplateDto | null;
  readonly context: IntakeContextDto | null;
  /** Krok NAB6 — jmeno zobrazene sablony a predvolba ze zakazky. */
  readonly templateKind?: ChecklistTemplateKind | null | undefined;
  readonly expectedKind?: ChecklistTemplateKind | null | undefined;
  readonly onTemplateKind?: ((kind: ChecklistTemplateKind) => void) | undefined;
  readonly marks: ChecklistState;
  /** `note` = puvod hodnoty, jde do provenience faktu. */
  readonly onMark: (factKey: string, value: unknown, note: string) => void;
  readonly onUnmark: (factKey: string) => void;
  /** Krok NAB4 — mikrofon u kazde skupiny (diktat do 60 s). */
  readonly recorder?: Recorder | undefined;
  readonly dictations?: DictationStates | undefined;
  readonly onDictate?: ((scopeKey: string, blob: Blob, durationS: number) => void) | undefined;
  readonly onAcceptSuggestion?: ((suggestion: DictationSuggestionDto) => void) | undefined;
  readonly onDismissSuggestion?: ((suggestion: DictationSuggestionDto) => void) | undefined;
}): React.JSX.Element {
  const [mode, setMode] = React.useState<ChecklistMode>('quick');
  const [onlyMissing, setOnlyMissing] = React.useState(false);
  /** Puvod hodnoty se prepina u skupiny; vychozi je z sablony. */
  const [origins, setOrigins] = React.useState<Readonly<Record<string, 'observed' | 'owner_claim'>>>({});

  if (template === null) {
    return (
      <section className="rh-v4-group rh-fi-card" data-testid="nab2-checklist-loading">
        <div className="rh-fi-card__sub">{tr('webAdmin.fieldIntake.checklistTab.loading')}</div>
      </section>
    );
  }

  /* Seznam sablon rika server (`/context`) — v bundlu adminu zadna kopie jmen. */
  const kinds = context?.checklistKindsAvailable ?? [];
  const answers = pointAnswers(template, marks);
  const covered = coveredPoints(template, context);
  const progress = checklistProgress(template, answers, covered);
  const groups = groupedPoints(template, answers, mode, covered);
  const missingIds = new Set(progress.sreality.missing.concat(progress.contract.missing).map((point) => point.id));

  const answer = (point: ChecklistPointDto, value: unknown): void => {
    if (point.factKey === null) return;
    const origin = origins[point.group] ?? point.defaultOrigin;
    onMark(point.factKey, value, tr(`webAdmin.fieldIntake.checklistTab.origin.${origin}` as Parameters<typeof t>[0]));
  };

  return (
    <div className="rh-fi-cl" data-testid="nab2-checklist">
      <section className="rh-v4-group rh-fi-cl__head">
        {templateKind !== null && onTemplateKind !== undefined && kinds.length > 1 ? (
          <TemplateSwitch
            current={templateKind}
            expected={expectedKind}
            available={kinds}
            onPick={onTemplateKind}
          />
        ) : null}
        <div className="rh-fi-cl__progress" data-testid="nab2-progress">
          <span data-testid="nab2-progress-sreality">
            {tr('webAdmin.fieldIntake.checklistTab.progressSreality', {
              done: progress.sreality.done,
              total: progress.sreality.total,
            })}
          </span>
          <span data-testid="nab2-progress-contract">
            {tr('webAdmin.fieldIntake.checklistTab.progressContract', {
              done: progress.contract.done,
              total: progress.contract.total,
            })}
          </span>
        </div>
        <div className="rh-fi-cl__modes">
          <button
            type="button"
            className={`rh-v4-btn rh-v4-btn--chip${mode === 'quick' ? ' is-on' : ''}`}
            aria-pressed={mode === 'quick'}
            onClick={() => setMode('quick')}
            data-testid="nab2-mode-quick"
          >
            {tr('webAdmin.fieldIntake.checklistTab.modeQuick')}
          </button>
          <button
            type="button"
            className={`rh-v4-btn rh-v4-btn--chip${mode === 'full' ? ' is-on' : ''}`}
            aria-pressed={mode === 'full'}
            onClick={() => setMode('full')}
            data-testid="nab2-mode-full"
          >
            {tr('webAdmin.fieldIntake.checklistTab.modeFull')}
          </button>
          <button
            type="button"
            className={`rh-v4-btn rh-v4-btn--tinted${onlyMissing ? ' is-on' : ''}`}
            aria-pressed={onlyMissing}
            onClick={() => setOnlyMissing((value) => !value)}
            data-testid="nab2-missing-toggle"
          >
            {tr('webAdmin.fieldIntake.checklistTab.whatIsMissing')}
          </button>
        </div>
        {onlyMissing && missingIds.size === 0 ? (
          <p className="rh-fi-note" data-testid="nab2-missing-none">
            {tr('webAdmin.fieldIntake.checklistTab.missingNone')}
          </p>
        ) : null}
        {onlyMissing && missingIds.size > 0 ? (
          <p className="rh-fi-note rh-fi-note--warn" data-testid="nab2-missing-count">
            <CircleAlert size={18} aria-hidden="true" />
            {tr('webAdmin.fieldIntake.checklistTab.missingCount', { count: missingIds.size })}
          </p>
        ) : null}
      </section>

      {groups.map((group) => {
        const shown = onlyMissing ? group.points.filter((point) => missingIds.has(point.id)) : group.points;
        if (shown.length === 0) return null;
        const origin = origins[group.id] ?? shown[0]?.defaultOrigin ?? 'observed';
        return (
          <details
            key={group.id}
            className="rh-v4-group rh-fi-cl__group"
            open={group.done < group.points.length}
            data-testid={`nab2-group-${group.id}`}
          >
            <summary className="rh-v4-row rh-fi-cl__groupHead" data-testid={`nab2-group-head-${group.id}`}>
              <span className="rh-v4-row__text">
                <span className="rh-v4-row__title">{group.title}</span>
              </span>
              <span className="rh-v4-row__value" data-testid={`nab2-group-count-${group.id}`}>
                {tr('webAdmin.fieldIntake.checklistTab.groupCount', { done: group.done, total: group.points.length })}
              </span>
            </summary>
            <div className="rh-fi-cl__origin">
              <span>{tr('webAdmin.fieldIntake.checklistTab.originLabel')}</span>
              {(['observed', 'owner_claim'] as const).map((value) => (
                <button
                  key={value}
                  type="button"
                  className={`rh-v4-btn rh-v4-btn--chip${origin === value ? ' is-on' : ''}`}
                  aria-pressed={origin === value}
                  onClick={() => setOrigins((prev) => ({ ...prev, [group.id]: value }))}
                  data-testid={`nab2-origin-${group.id}-${value}`}
                >
                  {tr(`webAdmin.fieldIntake.checklistTab.origin.${value}` as Parameters<typeof t>[0])}
                </button>
              ))}
            </div>
            {/*
              Krok NAB4 — MIKROFON U SKUPINY. Rozbor smi navrhnout jen do bodu
              TETO skupiny (seznam bodu a moznosti se posila jako omezeni),
              takze diktat u „Kuchyně" nesahne na pravni cast.
            */}
            {recorder !== undefined && onDictate !== undefined && context?.transcriptionAvailable === true ? (
              <DictationMic
                recorder={recorder}
                state={dictations[groupScopeKey(group.id)]}
                testid={`nab4-group-${group.id}`}
                onRecorded={(blob, durationS) => onDictate(groupScopeKey(group.id), blob, durationS)}
              />
            ) : null}
            {shown.map((point) => (
              <Point
                key={point.id}
                point={point}
                value={answers.get(point.id)}
                covered={covered.has(point.id)}
                prefill={prefillOf(point, context)}
                suggestion={suggestionForPoint(dictations, point.id)}
                onAnswer={(value) => answer(point, value)}
                onAcceptSuggestion={(suggestion) => onAcceptSuggestion?.(suggestion)}
                onDismissSuggestion={(suggestion) => onDismissSuggestion?.(suggestion)}
                onClear={() => {
                  if (point.factKey !== null) onUnmark(point.factKey);
                }}
              />
            ))}
          </details>
        );
      })}
    </div>
  );
}
